/**
 * The sync engine (issue #25, ADR-0017 and ADR-0018): what keeps this Device's Characters,
 * Bookmarks and Recents the same as the Account's, through the Worker of #24.
 *
 * The shape of it:
 *
 * - **Push after save, pull after push.** A save is announced through the record feed
 *   (`src/character/records.ts`), debounced, and pushed with the version the Account last gave
 *   this Device. Then the changes since the cursor are pulled. Push first, so a pull never meets
 *   a record with a local change on it: the Worker's conflict rule is the only thing that ever
 *   decides between two versions, and it never discards either.
 * - **Dirt is derived, never flagged.** A record whose `updatedAt` is not the one the
 *   bookkeeping row recorded has changed since the Account saw it. Nothing to set, nothing to
 *   clear, and a crash between a save and its bookkeeping cannot lose a change.
 * - **What cannot be applied is parked, not forced.** A Character with a Session open here, one
 *   from a Snapshot this Device has not installed, one from a newer build: the incoming record
 *   is kept whole with the reason and applied when that changes. A park whose record has been
 *   changed here since is dropped instead, and the push settles the two.
 * - **The loser of a conflict is kept.** Whichever Device pushes second is told, and keeps the
 *   losing version as a Conflict Copy: a Character of its own, named for the Device it came
 *   from, never pushed, and turned back into the Character by "Use this copy".
 *
 * Nothing here blocks on the network. Offline, a push stays pending and goes out at the next
 * trigger; a refusal that retrying cannot mend stops the engine and says so in the status.
 */
import type {
  AccountResponse,
  DeleteAccountTokenResponse,
  DocumentRef,
  PushDocument,
  PushResult,
  SyncCollection,
  SyncDocument,
} from "../../shared/sync-format";
import { SYNC_COLLECTIONS, SYNC_MAX_PUSH, TOMBSTONE_LIFETIME_MS } from "../../shared/sync-format";
import { newId } from "../character/create";
import { MigrationError, migrateCharacter } from "../character/migrate";
import type { Character } from "../character/model";
import { RECENTS_CAP } from "../character/personal";
import type { RecordChange } from "../character/records";
import { subscribeRecords } from "../character/records";
import { getSetting, putCharacter, setSetting } from "../character/store";
import { validateCharacter } from "../character/validate";
import { SyncClient, SyncError, SyncOffline, type SyncTransport } from "./client";
import type { ParkReason, SyncRecord, SyncSession } from "./model";
import { emptyRecord, trackingKey } from "./model";
import { conflictCopy, conflictOutcome, decideCharacterApply, mergeRecents, pendingFor, tombstoneExpired } from "./rules";
import { createSyncStatus, type SyncStatus } from "./status";
import { COLLECTIONS, applyPersonal, clearRows, dropRows, readRow, readRows, rowOf, writeRow } from "./tracking";

/** What the engine needs from the app around it, so a test can stand in for all of it. */
export interface EngineEnv {
  transport: SyncTransport;
  now(): number;
  /** The Snapshot release this Device runs (`manifest.snapshot.release`). */
  snapshotTag(): Promise<string>;
  /** Migrate a Character that arrived from an older Snapshot, as an import from a file is migrated (#17). */
  migrate(character: Character): Promise<Character>;
  /** How long a save waits before its push goes out. */
  debounceMs: number;
}

export interface SyncOutcome {
  pushed: number;
  pulled: number;
  conflicts: number;
  parked: number;
  /** Why it stopped short of finishing; null when it ran through. */
  stopped: string | null;
}

/** A Character kept as the losing half of a conflict (CONTEXT.md, "Conflict Copy"). */
export interface ConflictCopy {
  /** The Conflict Copy's own Character id. */
  id: string;
  /** The Character that kept the Account. */
  of: string;
  /** The name that Character has. */
  name: string;
  /** The Device the losing version was written on. */
  device: string;
  at: number;
}

/** A record the Account holds that this Device cannot apply yet. */
export interface ParkedRecord {
  collection: SyncCollection;
  id: string;
  name: string;
  reason: ParkReason;
  at: number;
}

/** A deletion this Device can still undo (CONTEXT.md, "Recently Deleted"). */
export interface DeletedRecord extends DocumentRef {
  name: string;
  at: number;
  /** The Device it was deleted on, as Recently Deleted names it (#26); null when unknown. */
  device: string | null;
  /** False once the thirty days are up: the Account no longer keeps the body. */
  restorable: boolean;
  /** How many whole days are left of the thirty; 0 on the last one, and 0 once it has expired. */
  daysLeft: number;
}

export interface RestoreOutcome {
  restored: DocumentRef[];
  /** Deletions the Account no longer holds: purged after thirty days, or never made. */
  missing: DocumentRef[];
}

export interface SyncEngine {
  status(): SyncStatus;
  subscribe(listener: (status: SyncStatus) => void): () => void;
  session(): Promise<SyncSession | null>;
  /** Listen to saves and deletions. Returns the unsubscribe. */
  watch(): () => void;
  /** Watch, follow the foreground, and sync once now. */
  start(): Promise<void>;
  stop(): void;
  /** Where to send the browser to sign in; `device` is what the Device will be called. */
  signInUrl(device: string): Promise<string>;
  /** Spend the claim the sign-in redirect came back with, then upload whatever this Device holds. */
  completeSignIn(claim: string): Promise<SyncSession>;
  signOut(options?: { removeLocal?: boolean }): Promise<void>;
  /** Push what is pending, then pull what has changed. `force` runs even when the engine is blocked. */
  syncNow(options?: { force?: boolean }): Promise<SyncOutcome>;
  account(): Promise<AccountResponse>;
  /** Remove one Device from the Account. Removing this one signs it out here as well (#26). */
  revokeDevice(id: string): Promise<void>;
  /** The confirmation `deleteAccount` asks for, which the app has to obtain first. */
  deleteAccountToken(): Promise<DeleteAccountTokenResponse>;
  /** Delete the whole Account. This Device is signed out; what it holds locally stays. */
  deleteAccount(confirmation: string): Promise<void>;
  conflictCopies(): Promise<ConflictCopy[]>;
  /** Make a Conflict Copy the Character again: it is pushed over the version that beat it. */
  useConflictCopy(id: string): Promise<void>;
  parked(): Promise<ParkedRecord[]>;
  /** Apply what a park was waiting for, if it can be applied now. */
  resumeParked(id?: string): Promise<void>;
  recentlyDeleted(): Promise<DeletedRecord[]>;
  restore(refs: DocumentRef[]): Promise<RestoreOutcome>;
}

const MAX_PULL_PAGES = 500;

function iso(at: number): string {
  return new Date(at).toISOString();
}

/** The Character a record holds, or null when this build cannot read it. */
function readCharacter(data: unknown): { character: Character } | { schema: number } | null {
  try {
    return { character: validateCharacter(migrateCharacter(data)) };
  } catch (e) {
    if (e instanceof MigrationError) {
      const schema = (data as { schema?: unknown } | null)?.schema;
      return { schema: typeof schema === "number" ? schema : 0 };
    }
    return null;
  }
}

export function createEngine(env: EngineEnv): SyncEngine {
  const status = createSyncStatus();
  const client = new SyncClient(env.transport);
  let loaded: SyncSession | null = null;
  let running: Promise<SyncOutcome> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const unsubscribes: (() => void)[] = [];

  async function load(): Promise<SyncSession | null> {
    loaded = await getSetting("sync");
    client.setToken(loaded?.token ?? null);
    return loaded;
  }

  async function save(session: SyncSession): Promise<SyncSession> {
    loaded = session;
    client.setToken(session.token);
    await setSetting("sync", session);
    return session;
  }

  // ---- bookkeeping ---------------------------------------------------------

  interface Owed {
    collection: SyncCollection;
    id: string;
    baseVersion: number | null;
    /** Set on a deletion: what the tombstone is stamped with. */
    deletedAt: number | null;
  }

  /**
   * What this Device owes the Account, from the bookkeeping rows and each record's `updatedAt`
   * alone, and the rows that no longer name anything. No record body is read here, so counting
   * what is pending costs a scan of the summaries rather than a load of every Character.
   */
  async function sweep(): Promise<{ owed: Owed[]; untrack: string[] }> {
    const owed: Owed[] = [];
    const untrack: string[] = [];
    for (const collection of SYNC_COLLECTIONS) {
      const adapter = COLLECTIONS[collection];
      const rows = new Map((await readRows(collection)).map((row) => [row.id, row]));
      const here = new Set<string>();
      for (const ref of await adapter.refs()) {
        here.add(ref.id);
        const row = rows.get(ref.id);
        if (pendingFor(row, ref.updatedAt, adapter.inferDeletes) !== "save") continue;
        owed.push({ collection, id: ref.id, baseVersion: row?.version ?? null, deletedAt: null });
      }
      for (const row of rows.values()) {
        if (here.has(row.id)) continue;
        const pending = pendingFor(row, null, adapter.inferDeletes);
        if (pending === "delete") {
          owed.push({ collection, id: row.id, baseVersion: row.version, deletedAt: row.deleted?.at ?? env.now() });
        } else if (pending === "untrack") {
          untrack.push(row.key);
        }
      }
    }
    return { owed, untrack };
  }

  /** The bodies to send. A record that cannot be read is left out rather than pushed empty. */
  async function documentsFor(owed: Owed[]): Promise<PushDocument[]> {
    const documents: PushDocument[] = [];
    for (const one of owed) {
      if (one.deletedAt !== null) {
        documents.push({ collection: one.collection, id: one.id, baseVersion: one.baseVersion, updatedAt: one.deletedAt, deleted: true });
        continue;
      }
      const local = await COLLECTIONS[one.collection].read(one.id);
      if (!local) continue;
      documents.push({ collection: one.collection, id: one.id, baseVersion: one.baseVersion, updatedAt: local.updatedAt, data: local.data });
    }
    return documents;
  }

  async function pendingCount(): Promise<number> {
    return (await sweep()).owed.length;
  }

  // ---- applying what the Account holds -------------------------------------

  type Applied = "applied" | "parked" | "deleted" | "skipped";

  async function park(row: SyncRecord, document: SyncDocument, reason: ParkReason): Promise<Applied> {
    await writeRow({ ...row, parked: { document, reason, at: env.now() } });
    return "parked";
  }

  async function applyCharacter(document: SyncDocument, row: SyncRecord): Promise<Applied> {
    const read = readCharacter(document.data);
    if (read === null) {
      console.warn(`sync: the Account's copy of Character ${document.id} could not be read; it was left alone`);
      return "skipped";
    }
    if (!("character" in read)) return park(row, document, { kind: "app", schema: read.schema });
    const incoming = read.character;
    const local = await COLLECTIONS.character.read(document.id);
    const sessionOpenHere = row.wroteHere && (local?.data as Character | undefined)?.session != null;
    const decision = decideCharacterApply({ incomingTag: incoming.snapshotTag, deviceTag: await env.snapshotTag(), sessionOpenHere });
    if (decision.kind === "park") return park(row, document, decision.reason);
    // One from an older Snapshot is migrated on arrival, as an import from a file is. That makes
    // it a change of its own, saved under this Device's clock, so the row below leaves it
    // looking dirty and the next push carries it back under the version that brought it here.
    const character = decision.kind === "migrate" ? await env.migrate(incoming) : incoming;
    await putCharacter(character, decision.kind === "migrate" ? iso(env.now()) : character.updatedAt);
    await writeRow({ ...row, version: document.version, syncedAt: document.updatedAt, wroteHere: false, from: document.deviceId, deleted: null, parked: null });
    return "applied";
  }

  /**
   * Write one document the Account holds. A record with a local change on it is left alone: the
   * push that follows carries it to the Worker, whose conflict rule decides between the two.
   * `force` is for a document the Worker has already decided on (a conflict winner, a restore).
   */
  async function applyDocument(document: SyncDocument, force = false): Promise<Applied> {
    const adapter = COLLECTIONS[document.collection];
    const row = await rowOf(document.collection, document.id);
    const local = await adapter.read(document.id);
    if (!force) {
      const pending = pendingFor({ ...row, parked: null }, local?.updatedAt ?? null, adapter.inferDeletes);
      if (pending === "save" || pending === "delete") return "skipped";
    }

    if (document.deletedAt !== null) {
      // The row is written before the record goes, so the deletion this makes locally is one the
      // feed's own handler finds already accounted for: it is the Account's deletion, made on
      // another Device, and not one this Device has to push or to claim (#25, #26).
      await writeRow({
        ...row,
        version: document.version,
        syncedAt: document.updatedAt,
        wroteHere: false,
        from: document.deviceId,
        deleted: { at: document.deletedAt, pushed: true, data: document.data, device: document.deviceId },
        parked: null,
      });
      if (local) await adapter.remove(document.id);
      return "deleted";
    }

    if (document.collection === "character") return applyCharacter(document, row);

    const at = await applyPersonal(document.collection, document.id, document.data);
    if (at === null) return "skipped";
    await writeRow({ ...row, version: document.version, syncedAt: at, wroteHere: false, from: document.deviceId, deleted: null, parked: null });
    return "applied";
  }

  // ---- conflicts -----------------------------------------------------------

  async function deviceName(session: SyncSession, id: string | null): Promise<string> {
    if (!id) return "another device";
    const held = session.deviceNames[id];
    if (held) return held;
    try {
      const account = await client.account();
      const names = Object.fromEntries(account.devices.map((device) => [device.id, device.name]));
      await save({ ...session, deviceNames: { ...session.deviceNames, ...names } });
      return names[id] ?? "another device";
    } catch {
      return "another device";
    }
  }

  /**
   * Keep the losing half of a conflict as a Character of its own: a new id, a name that says
   * where it came from, and everything else (the Roll Log included) as it was. Never pushed.
   */
  async function keepConflictCopy(session: SyncSession, ref: DocumentRef, data: unknown, from: string | null): Promise<boolean> {
    if (ref.collection !== "character") return false;
    const read = readCharacter(data);
    if (read === null || !("character" in read)) return false;
    const at = env.now();
    const device = await deviceName(session, from);
    const copy = conflictCopy(read.character, newId(), device, at);
    const saved = await putCharacter(copy, copy.updatedAt);
    await writeRow({
      ...emptyRecord("character", saved.id),
      syncedAt: Date.parse(saved.updatedAt),
      conflict: { of: ref.id, name: read.character.name, device, at },
    });
    return true;
  }

  /** Record what the Worker made of one pushed document. True when it was a conflict. */
  async function applyPushResult(session: SyncSession, sent: PushDocument, result: PushResult): Promise<boolean> {
    const row = await rowOf(sent.collection, sent.id);
    if (result.status === "stored") {
      const deleted = row.deleted ? { ...row.deleted, pushed: true } : null;
      await writeRow({ ...row, version: result.version, syncedAt: sent.updatedAt, wroteHere: true, from: null, deleted });
      return false;
    }
    const outcome = conflictOutcome(result);
    if (outcome.kind === "won") {
      const deleted = row.deleted ? { ...row.deleted, pushed: true } : null;
      await writeRow({ ...row, version: outcome.winner.version, syncedAt: sent.updatedAt, wroteHere: true, from: null, deleted });
      await keepConflictCopy(session, sent, outcome.displaced.data, outcome.displaced.deviceId);
      return true;
    }
    // The Account kept what it had. Ours becomes the Conflict Copy and the winner is written
    // here; `syncedAt` takes the version we sent, so a park leaves nothing looking dirty.
    if (!sent.deleted) await keepConflictCopy(session, sent, outcome.mine.data, outcome.mine.deviceId);
    await writeRow({
      ...row,
      version: outcome.winner.version,
      syncedAt: sent.updatedAt,
      wroteHere: true,
      parked: null,
      deleted: row.deleted ? { ...row.deleted, pushed: true } : null,
    });
    await applyDocument(outcome.winner, true);
    return true;
  }

  // ---- push and pull -------------------------------------------------------

  async function push(session: SyncSession): Promise<{ pushed: number; conflicts: number }> {
    const swept = await sweep();
    await dropRows(swept.untrack);
    const documents = await documentsFor(swept.owed);
    let conflicts = 0;
    for (let at = 0; at < documents.length; at += SYNC_MAX_PUSH) {
      const chunk = documents.slice(at, at + SYNC_MAX_PUSH);
      const results = await client.push(chunk);
      for (const [index, result] of results.entries()) {
        const sent = chunk.find((document) => document.collection === result.collection && document.id === result.id) ?? chunk[index]!;
        if (await applyPushResult(session, sent, result)) conflicts++;
      }
    }
    return { pushed: documents.length, conflicts };
  }

  async function pull(session: SyncSession): Promise<{ pulled: number; parked: number }> {
    let cursor = session.cursor;
    let pulled = 0;
    let parked = 0;
    const recents: { uuid: string; viewedAt: number }[] = [];
    for (let page = 0; page < MAX_PULL_PAGES; page++) {
      const answer = await client.pull(cursor);
      for (const document of answer.documents) {
        const applied = await applyDocument(document);
        if (applied === "parked") parked++;
        if (applied !== "skipped") pulled++;
        if (document.collection === "recent" && applied === "applied") recents.push({ uuid: document.id, viewedAt: document.updatedAt });
      }
      cursor = answer.cursor;
      await save({ ...(loaded ?? session), cursor });
      if (!answer.more) break;
    }
    // Each Recent was settled by time as it arrived; the cap of fifty is this Device's alone, so
    // what falls off here is untracked rather than deleted from the Account (ADR-0017).
    if (recents.length > 0) {
      const local = (await COLLECTIONS.recent.refs()).map((ref) => ({ uuid: ref.id, viewedAt: ref.updatedAt }));
      const { dropped } = mergeRecents(local, recents, RECENTS_CAP);
      for (const uuid of dropped) await COLLECTIONS.recent.remove(uuid);
      await dropRows(dropped.map((uuid) => trackingKey("recent", uuid)));
    }
    return { pulled, parked };
  }

  async function pruneTrash(): Promise<void> {
    const now = env.now();
    const expired = (await readRows()).filter((row) => row.deleted && row.deleted.pushed && tombstoneExpired(row.deleted.at, now));
    await dropRows(expired.map((row) => row.key));
  }

  async function resumeParked(id?: string): Promise<void> {
    const rows = (await readRows()).filter((row) => row.parked !== null && (id === undefined || row.id === id));
    for (const row of rows) {
      const document = row.parked!.document;
      const adapter = COLLECTIONS[row.collection];
      const local = await adapter.read(row.id);
      await writeRow({ ...row, parked: null });
      // A record deleted here since the park is not one to bring back.
      if (row.deleted) continue;
      // A local change since the park is the Worker's to weigh, not ours: leave it to the push.
      const pending = pendingFor({ ...row, parked: null }, local?.updatedAt ?? null, adapter.inferDeletes);
      if (pending === "save" || pending === "delete") continue;
      // And a park the Account has already moved past is history, not an update.
      if (row.version !== null && document.version <= row.version) continue;
      await applyDocument(document);
    }
  }

  // ---- the run ------------------------------------------------------------

  async function run(session: SyncSession): Promise<SyncOutcome> {
    await resumeParked();
    const pushed = await push(session);
    const pulled = await pull(loaded ?? session);
    // What arriving changed here — a Character migrated on arrival, a park resumed — goes back
    // in the same pass rather than waiting for the next trigger.
    const again = pulled.pulled > 0 ? await push(loaded ?? session) : { pushed: 0, conflicts: 0 };
    await pruneTrash();
    return {
      pushed: pushed.pushed + again.pushed,
      conflicts: pushed.conflicts + again.conflicts,
      pulled: pulled.pulled,
      parked: pulled.parked,
      stopped: null,
    };
  }

  async function syncNow(options: { force?: boolean } = {}): Promise<SyncOutcome> {
    if (running) return running;
    running = (async (): Promise<SyncOutcome> => {
      const session = await load();
      if (!session) {
        status.set({ phase: "off" });
        return { pushed: 0, pulled: 0, conflicts: 0, parked: 0, stopped: "this Device is not signed in" };
      }
      if (session.blocked && !options.force) {
        status.set({ phase: "blocked", code: session.blocked.code, message: session.blocked.message, pending: await pendingCount() });
        return { pushed: 0, pulled: 0, conflicts: 0, parked: 0, stopped: session.blocked.message };
      }
      if (session.blocked) await save({ ...session, blocked: null });
      status.set({ phase: "syncing", pending: await pendingCount() });
      try {
        const outcome = await run(loaded ?? session);
        const at = env.now();
        await save({ ...(loaded ?? session), syncedAt: at, blocked: null });
        const rows = await readRows();
        status.set({
          phase: "idle",
          at,
          pending: await pendingCount(),
          conflicts: rows.filter((row) => row.conflict !== null).length,
          parked: rows.filter((row) => row.parked !== null).length,
        });
        return outcome;
      } catch (e) {
        return failed(loaded ?? session, e);
      }
    })().finally(() => {
      running = null;
    });
    return running;
  }

  async function failed(session: SyncSession, error: unknown): Promise<SyncOutcome> {
    const stopped = error instanceof Error ? error.message : String(error);
    const outcome: SyncOutcome = { pushed: 0, pulled: 0, conflicts: 0, parked: 0, stopped };
    const pending = await pendingCount();
    if (error instanceof SyncOffline) {
      status.set({ phase: "offline", at: session.syncedAt, pending });
      return outcome;
    }
    if (error instanceof SyncError && error.blocking) {
      await save({ ...session, blocked: { code: error.code, message: error.message } });
      status.set({ phase: "blocked", code: error.code, message: error.message, pending });
      return outcome;
    }
    status.set({ phase: "failed", message: stopped, at: env.now(), pending });
    return outcome;
  }

  // ---- triggers ------------------------------------------------------------

  function schedule(): void {
    if (timer !== null) return;
    timer = setTimeout(() => {
      timer = null;
      void syncNow();
    }, env.debounceMs);
    // Node keeps the process alive for a pending timer; the app's own timers never unref.
    (timer as unknown as { unref?: () => void }).unref?.();
  }

  /** A deletion is the last moment the record can be copied into Recently Deleted. */
  async function noteDeletion(change: RecordChange): Promise<void> {
    const row = await readRow(change.collection, change.id);
    if (!row) return;
    if (row.conflict || row.version === null) {
      await dropRows([row.key]);
      return;
    }
    if (row.deleted) return;
    await writeRow({ ...row, deleted: { at: env.now(), pushed: false, data: change.before ?? null, device: loaded?.device.id ?? null }, parked: null });
  }

  async function onRecord(change: RecordChange): Promise<void> {
    if (!loaded) return;
    if (change.kind === "deleted") await noteDeletion(change);
    schedule();
  }

  function watch(): () => void {
    const off = subscribeRecords((change) => {
      void onRecord(change);
    });
    unsubscribes.push(off);
    return off;
  }

  async function signOut(options: { removeLocal?: boolean } = {}): Promise<void> {
    const session = await load();
    if (!session) return;
    try {
      await client.signOut();
    } catch {
      // The Device Token is dropped here whatever the Worker says; a stale row on the Account
      // is removed from the Account screen (#26).
    }
    if (options.removeLocal) {
      for (const collection of SYNC_COLLECTIONS) {
        const adapter = COLLECTIONS[collection];
        for (const ref of await adapter.refs()) await adapter.remove(ref.id);
      }
    }
    // Conflict Copies are this Device's own Characters and outlive the Account.
    await clearRows((row) => (row.conflict ? { ...emptyRecord(row.collection, row.id), conflict: row.conflict } : null));
    await setSetting("sync", null);
    loaded = null;
    client.setToken(null);
    status.set({ phase: "off" });
  }

  function stop(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    for (const off of unsubscribes.splice(0)) off();
  }

  // ---- the surface ---------------------------------------------------------

  return {
    status: () => status.get(),
    subscribe: (listener) => status.subscribe(listener),
    session: () => load(),
    watch,
    stop,
    async start() {
      await load();
      watch();
      if (typeof document !== "undefined") {
        const onVisible = (): void => {
          if (document.visibilityState === "visible") void syncNow();
        };
        document.addEventListener("visibilitychange", onVisible);
        unsubscribes.push(() => document.removeEventListener("visibilitychange", onVisible));
      }
      if (loaded) await syncNow();
      else status.set({ phase: "off" });
    },
    async signInUrl(device) {
      const session = await load();
      return client.signInUrl(device, session?.device.id ?? null);
    },
    async completeSignIn(claim) {
      let answer;
      try {
        answer = await client.claim(claim);
      } catch (e) {
        status.set({ phase: "failed", message: e instanceof Error ? e.message : String(e), at: env.now(), pending: await pendingCount() });
        throw e;
      }
      const previous = await load();
      // A different Account on this Device starts from nothing: the old Account's bookkeeping
      // says nothing about this one's versions.
      if (previous && previous.account.id !== answer.account.id) await clearRows((row) => (row.conflict ? { ...emptyRecord(row.collection, row.id), conflict: row.conflict } : null));
      const session = await save({
        token: answer.token,
        account: { id: answer.account.id, issuer: answer.account.issuer, email: answer.account.email, name: answer.account.name },
        device: { id: answer.device.id, name: answer.device.name },
        cursor: previous && previous.account.id === answer.account.id ? previous.cursor : "",
        deviceNames: { [answer.device.id]: answer.device.name },
        signedInAt: env.now(),
        syncedAt: null,
        blocked: null,
      });
      try {
        const account = await client.account();
        await save({ ...session, deviceNames: Object.fromEntries(account.devices.map((device) => [device.id, device.name])) });
      } catch {
        // The Account's Device names are a nicety; the sign-in stands without them.
      }
      // Everything this Device already holds has no bookkeeping row, so it is pending by
      // definition: the first sync uploads it all, and an id the Account already holds goes
      // through the conflict rule like any other.
      await syncNow({ force: true });
      return loaded ?? session;
    },
    signOut,
    syncNow,
    async account() {
      await load();
      return client.account();
    },
    async revokeDevice(id) {
      const session = await load();
      await client.revokeDevice(id);
      // Revoking this Device's own token is a sign-out: the token it still holds is worthless.
      if (session && id === session.device.id) await signOut();
    },
    async deleteAccountToken() {
      await load();
      return client.deleteAccountToken();
    },
    async deleteAccount(confirmation) {
      await load();
      await client.deleteAccount(confirmation);
      // The Account is gone, so there is nothing left to sign out of; what this Device holds
      // stays, as device-only Characters (#26).
      await signOut();
    },
    async conflictCopies() {
      return (await readRows("character"))
        .filter((row) => row.conflict !== null)
        .map((row) => ({ id: row.id, of: row.conflict!.of, name: row.conflict!.name, device: row.conflict!.device, at: row.conflict!.at }))
        .sort((a, b) => b.at - a.at);
    },
    async useConflictCopy(id) {
      const row = await readRow("character", id);
      if (!row?.conflict) throw new Error(`Character ${id} is not a Conflict Copy`);
      const copy = await COLLECTIONS.character.read(id);
      if (!copy) throw new Error(`no Character ${id}`);
      const { of, name } = row.conflict;
      const at = env.now();
      // The two change places rather than one writing over the other: whatever the Character
      // holds now becomes the Conflict Copy in turn, so "Use this copy" is reversible and no
      // version is ever lost (#26).
      const standing = await COLLECTIONS.character.read(of);
      if (standing) {
        const session = loaded ?? (await load());
        const standingRow = await rowOf("character", of);
        // Named for where the version stepping aside came from: the Device that sent it, or
        // this one — which is what a version written or chosen here honestly is, whether or not
        // it has since come back through this Device's own pull.
        const came = standingRow.from ?? null;
        const device = !session || standingRow.wroteHere || came === null || came === session.device.id ? "this device" : await deviceName(session, came);
        const displaced = conflictCopy(standing.data as Character, newId(), device, at);
        const saved = await putCharacter(displaced, displaced.updatedAt);
        await writeRow({ ...emptyRecord("character", saved.id), syncedAt: Date.parse(saved.updatedAt), conflict: { of, name, device, at } });
      }
      // The copy becomes the Character again under the winner's id, saved now, so it is the
      // later change wherever the Account's version stands.
      await putCharacter({ ...(copy.data as Character), id: of, name }, iso(at));
      await COLLECTIONS.character.remove(id);
      await dropRows([row.key]);
      await syncNow();
    },
    async parked() {
      const rows = (await readRows()).filter((row) => row.parked !== null);
      return rows.map((row) => {
        const parked = row.parked!;
        const data = parked.document.data as { name?: unknown; uuid?: unknown } | null;
        const name = typeof data?.name === "string" ? data.name : typeof data?.uuid === "string" ? data.uuid : row.id;
        return { collection: row.collection, id: row.id, name, reason: parked.reason, at: parked.at };
      });
    },
    resumeParked,
    async recentlyDeleted() {
      const now = env.now();
      const session = loaded ?? (await load());
      const named = (id: string | null | undefined): string | null => {
        if (!id) return null;
        if (id === session?.device.id) return "this device";
        return session?.deviceNames[id] ?? "another device";
      };
      return (await readRows())
        .filter((row) => row.deleted !== null)
        .map((row) => {
          const deleted = row.deleted!;
          const data = deleted.data as { name?: unknown } | null;
          const expired = tombstoneExpired(deleted.at, now);
          return {
            collection: row.collection,
            id: row.id,
            name: typeof data?.name === "string" ? data.name : row.id,
            at: deleted.at,
            device: named(deleted.device),
            restorable: !expired,
            daysLeft: expired ? 0 : Math.max(0, Math.floor((deleted.at + TOMBSTONE_LIFETIME_MS - now) / 86_400_000)),
          };
        })
        .sort((a, b) => b.at - a.at);
    },
    async restore(refs) {
      await load();
      const answer = await client.restore(refs);
      const outcome: RestoreOutcome = { restored: [], missing: [] };
      for (const result of answer.results) {
        if (result.status === "restored") {
          await applyDocument(result.document, true);
          outcome.restored.push({ collection: result.collection, id: result.id });
        } else {
          await dropRows([trackingKey(result.collection, result.id)]);
          outcome.missing.push({ collection: result.collection, id: result.id });
        }
      }
      return outcome;
    },
  };
}
